package dev.kuml.portal.web.i18n

import kotlinx.browser.document
import kotlinx.browser.window

/**
 * R-18's single source of truth for "which language is the UI in right now". Deliberately a plain
 * mutable object, not a `MutableState`/Flow -- this codebase has no reactive-binding system
 * anywhere (every screen is built once, imperatively, and torn down/rebuilt wholesale on the next
 * navigation, see `PortalApp.renderScreen`'s KDoc), so a language switch follows the SAME
 * rebuild-the-current-screen convention via [onLanguageChanged] rather than introducing a second,
 * novel reactivity mechanism just for this.
 */
public object LanguageState {
    public var current: Language = Language.EN
        private set

    /** The active [Strings] bundle -- read this at the point a widget is BUILT, never cached across
     * a language change (see class KDoc: every screen is fully rebuilt on switch, so reading it at
     * build time is always correct; see also the [t] accessor below). */
    public val strings: Strings get() = if (current == Language.DE) StringsDe else StringsEn

    /** Exactly ONE subscriber, registered once by `PortalApp.start()` -- deliberately a single
     * nullable callback, not a list: nothing in this app ever needs a second subscriber, and a
     * growing listener list would be an unbounded-resource hazard in a long-lived SPA session. */
    public var onLanguageChanged: (() -> Unit)? = null

    /** Resolves the starting language (persisted choice, else browser detection -- see this wave's
     * plan §2/§3) and applies it to `document.documentElement.lang`. Must be called exactly once,
     * before the first screen is rendered (see `PortalApp.start()`). Does NOT invoke
     * [onLanguageChanged] -- there is nothing subscribed yet at this point in startup, and the
     * caller renders the first screen itself immediately afterwards. */
    public fun initialise() {
        val stored = LanguageStore.load()
        current = stored ?: LanguageDetection.fromBrowserLanguages(browserLanguageTags())
        applyHtmlLang()
    }

    /** Switches the active language, persists the choice, and notifies [onLanguageChanged] -- a
     * no-op (no rebuild, no redundant write) if [language] is already [current]. */
    public fun set(language: Language) {
        if (language == current) return
        current = language
        LanguageStore.save(language)
        applyHtmlLang()
        onLanguageChanged?.invoke()
    }

    private fun applyHtmlLang() {
        document.documentElement?.setAttribute("lang", current.htmlLang)
    }

    /** `navigator.languages`, falling back to a one-element list from `navigator.language`, falling
     * back to an empty list -- both properties are technically nullable/absent in some embedded
     * JS environments, and [LanguageDetection.fromBrowserLanguages] already treats an empty list
     * correctly (falls back to [Language.EN], see its KDoc). */
    private fun browserLanguageTags(): List<String> {
        val languages = window.navigator.languages
        if (languages.isNotEmpty()) return languages.toList()
        val single = window.navigator.language
        return listOf(single)
    }
}

/** Short accessor for the currently active language's [Strings] bundle -- read this at the point a
 * widget is BUILT (see [LanguageState.strings]'s KDoc for why caching it across a language change
 * is never safe, and why it never needs to be). */
public val t: Strings get() = LanguageState.strings
